/**
 * G13. Зависимости: «Отказ». Раз в год при привычке (курение, алкоголь,
 * азарт) или тяге ≥ REFUSAL_MIN_URGE — кнопка «Не сегодня» и таймер
 * REFUSAL_HOLD_MS; нужно удержать нажатой. Сила тяги `strength` — максимум
 * из `flags.urge` и REFUSAL_HABIT_STRENGTH при привычке [моё]; если она
 * > REFUSAL_ESCAPE_STRENGTH, кнопка «уезжает» из-под пальца в момент
 * `escapeAtMs` (чем сильнее тяга, тем раньше), и её надо поймать снова
 * (`input.caught`) в пределах `catchMs`. Удержал — тяга −5, нет — +2.
 * Блок помощи (`helpKey`) показывается всегда.
 *
 * «Пропустить» — без изменений тяги: автопилот не должен ни лечить, ни
 * усиливать зависимость за игрока (спека даёт только ±, ожидание без
 * бонусов — ноль).
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { urgeOf, type Life, type MinigameOutcome } from '@life/engine';
import type { MinigameDef } from '../types';
import { nothing } from './common';

export const REFUSAL_MIN_AGE = 12;
export const REFUSAL_MAX_AGE = 100;
export const REFUSAL_HOLD_MS = 10_000;
export const REFUSAL_CATCH_MS = 1500;
/** Тяга, с которой игра предлагается и без привычки. */
export const REFUSAL_MIN_URGE = 40;
/** Сила, выше которой кнопка уезжает. */
export const REFUSAL_ESCAPE_STRENGTH = 60;
/** Сила привычки (курение/алкоголь/азарт как привычка) для расчёта «уезжания». */
export const REFUSAL_HABIT_STRENGTH = 65;
export const REFUSAL_SUCCESS_URGE = -5;
export const REFUSAL_FAIL_URGE = 2;
export const REFUSAL_HELP_KEY = 'minigame.refusal.help';
export const REFUSAL_HABITS = ['smoking', 'alcohol', 'gambling'] as const;
export type RefusalHabit = (typeof REFUSAL_HABITS)[number] | 'urge';

export interface RefusalState {
  habit: RefusalHabit;
  holdMs: number;
  strength: number;
  /** Момент, когда кнопка уезжает, или null, если не уезжает. */
  escapeAtMs: number | null;
  catchMs: number;
  helpKey: string;
}

export type RefusalInput = { heldMs: number; caught?: boolean };

/** Первая из привычек жизни или «тяга» без привычки. */
export function refusalHabit(life: Life): RefusalHabit {
  for (const h of REFUSAL_HABITS) if (life.habits[h]) return h;
  return 'urge';
}

export function refusalStrength(life: Life): number {
  const habit = refusalHabit(life) !== 'urge';
  return Math.max(urgeOf(life), habit ? REFUSAL_HABIT_STRENGTH : 0);
}

/** Момент «уезжания»: при силе 61..100 — от почти конца до половины таймера. */
export function refusalEscapeAt(strength: number, holdMs: number = REFUSAL_HOLD_MS): number | null {
  if (strength <= REFUSAL_ESCAPE_STRENGTH) return null;
  const over = Math.min(100, strength) - REFUSAL_ESCAPE_STRENGTH;
  return Math.round(holdMs * (1 - over / ((100 - REFUSAL_ESCAPE_STRENGTH) * 2)));
}

export const refusal: MinigameDef<RefusalInput, RefusalState> = {
  id: 'refusal',
  section: 'G',
  triggers: [{ kind: 'age', min: REFUSAL_MIN_AGE, max: REFUSAL_MAX_AGE, everyYears: 1 }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return refusalHabit(life) !== 'urge' || urgeOf(life) >= REFUSAL_MIN_URGE;
  },
  setup(life: Life): RefusalState {
    const strength = refusalStrength(life);
    return { habit: refusalHabit(life), holdMs: REFUSAL_HOLD_MS, strength, escapeAtMs: refusalEscapeAt(strength), catchMs: REFUSAL_CATCH_MS, helpKey: REFUSAL_HELP_KEY };
  },
  play(state: RefusalState, input: RefusalInput): MinigameOutcome {
    const held = Number.isFinite(input.heldMs) && input.heldMs >= state.holdMs && (state.escapeAtMs === null || input.caught === true);
    return held
      ? { ...nothing(), urgeDelta: REFUSAL_SUCCESS_URGE, logKey: 'minigame.refusal.held' }
      : { ...nothing(), urgeDelta: REFUSAL_FAIL_URGE, logKey: 'minigame.refusal.slipped' };
  },
  expected(): MinigameOutcome {
    return nothing();
  },
};
